import { fetchCancellationReasons } from "@campgladiator/cg-common.api.cancellation-reason";
import {
  CancellationReason,
  SubscriptionDTO,
} from "@campgladiator/cg-common.types.types";
import { Loader } from "@campgladiator/cgui-core.atoms.loader";
import { ButtonProps } from "@campgladiator/cgui-core.atoms.button";
import { Option } from "@campgladiator/cgui-core.atoms.dropdown";
import React, { useCallback, useEffect, useState } from "react";

export type UseWithCancelMembershipModalProps = {
  onClose?: (success?: boolean) => void;
  subscription: SubscriptionDTO;
  onFormSubmit?: (reason: CancellationReason) => void;
  isLoading: boolean;
  theme: ButtonProps["theme"];
};

const useWithCancelMembershipModal = ({
  onClose,
  subscription,
  onFormSubmit,
  isLoading,
  theme,
}: UseWithCancelMembershipModalProps) => {
  const isTrial = subscription.status === "TRIAL";
  const introModalTitleSubscriptionType = isTrial ? "Trial" : "Membership";
  const introModalTitle = `Set ${introModalTitleSubscriptionType} to Cancel?`;
  const [modalTitle, setModalTitle] = useState<string>(introModalTitle);
  const [isToCancel, setIsToCancel] = useState<boolean>(false);
  const [selectedReason, setSelectedReason] =
    useState<CancellationReason | null>(null);
  const [cancellationReasonOptions, setCancellationReasonOptions] = useState<
    Option[]
  >([]);
  const handleSelectReasonOption = (option?: Option) => {
    setSelectedReason(!!option ? (option.itemId as CancellationReason) : null);
  };

  const cancelRetainText =
    theme === "trainer" &&
    `Would the Camper prefer 1 month free or an extended freeze? If so, stop and contact Camper Support, and they will complete the request for you.`;
  const cancellationDisclaimerText = isTrial
    ? "Trial will cancel at the end of it's current period"
    : "Membership will cancel at the completion of it's current term";
  const cancellationDateText = isTrial ? "Trial End Date" : "End Date";

  const modalButtons: ButtonProps[] = [
    {
      emphasis: "primary",
      variation: "text",
      children: "Go Back",
      disabled: false,
      onClick: () => {
        setIsToCancel(false);
        if (!isToCancel) onClose(undefined);
      },
      theme,
    },
    {
      emphasis: "primary",
      variation: "solid",
      children: isLoading ? <Loader /> : isToCancel ? "Confirm" : "Continue",
      disabled: isToCancel ? !selectedReason || isLoading : false,
      onClick: () =>
        isToCancel ? onFormSubmit(selectedReason) : setIsToCancel(true),
      theme,
    },
  ];

  const getCancellationReasonOptions = useCallback(() => {
    fetchCancellationReasons().then((reasons) => {
      const options = Object.entries(reasons).map(([key, value]) => {
        return {
          text: value as string,
          itemId: key,
          type: "item",
          theme: "core",
        };
      });
      setCancellationReasonOptions(options as Option[]);
    });
  }, []);

  useEffect(() => {
    setModalTitle(isToCancel ? "Cancel Reason?" : introModalTitle);
  }, [isToCancel]);

  useEffect(() => {
    if (!cancellationReasonOptions?.length) getCancellationReasonOptions();
  }, [subscription]);

  return {
    cancelRetainText,
    modalTitle,
    modalButtons,
    isToCancel,
    cancellationReasonOptions,
    cancellationDateText,
    cancellationDisclaimerText,
    handleSelectReasonOption,
  };
};

export default useWithCancelMembershipModal;
